'use client'

import React from 'react'
import { Panel } from './ui/layout'

interface SystemStatusPanelProps {
  isInitialized: boolean
  audioContextState?: AudioContextState
  initializationError?: string | null
  fps: number
}

const SystemStatusPanel: React.FC<SystemStatusPanelProps> = ({
  isInitialized,
  audioContextState,
  initializationError,
  fps
}) => {
  const getPerformanceColor = (fps: number) => {
    if (fps >= 50) return 'text-green-400'
    if (fps >= 30) return 'text-yellow-400'
    return 'text-red-400'
  }

  return (
    <Panel title="System Core" icon="🚀">
      <div className="space-y-4">
        {/* System Online Indicator */}
        <div className="flex items-center justify-center px-3 py-2 bg-gradient-to-r from-slate-800/50 to-slate-700/50 backdrop-blur-sm border border-slate-600/50 rounded-lg">
          <div className="w-3 h-3 bg-emerald-400 rounded-full shadow-lg shadow-emerald-400/30 mr-2"></div>
          <span className="text-sm font-mono text-emerald-400 font-medium">SYSTEM ONLINE</span>
        </div>

        {/* Audio Engine Status */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-slate-200">Audio Engine</span>
            <div className={`w-3 h-3 rounded-full ${
              isInitialized && audioContextState === 'running' ? 'bg-emerald-400 shadow-lg shadow-emerald-400/30' :
              isInitialized && audioContextState === 'suspended' ? 'bg-amber-400 animate-pulse shadow-lg shadow-amber-400/30' :
              initializationError ? 'bg-red-500 shadow-lg shadow-red-500/30' :
              'bg-amber-400 animate-pulse shadow-lg shadow-amber-400/30'
            }`}></div>
          </div>
          <span className="text-xs font-mono text-slate-400">
            {isInitialized && audioContextState === 'running' ? 'ACTIVE' :
             isInitialized && audioContextState === 'suspended' ? 'WAITING' :
             initializationError ? 'ERROR' :
             'BOOTING'}
          </span>
        </div>

        {/* CPU/Memory Status */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-slate-200">Processing</span>
            <div className="w-3 h-3 bg-cyan-400 rounded-full shadow-lg shadow-cyan-400/30"></div>
          </div>
          <span className="text-xs font-mono text-slate-400">IDLE</span>
        </div>

        {/* Pipeline Status */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-slate-200">Pipeline</span>
            <div className="w-3 h-3 bg-purple-400 rounded-full shadow-lg shadow-purple-400/30"></div>
          </div>
          <span className="text-xs font-mono text-slate-400">READY</span>
        </div>

        {/* Performance Status */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-slate-200">Performance</span>
            <div className={`w-3 h-3 rounded-full shadow-lg ${
              fps >= 50 ? 'bg-green-400 shadow-green-400/30' :
              fps >= 30 ? 'bg-yellow-400 shadow-yellow-400/30' :
              'bg-red-400 shadow-red-400/30'
            }`}></div>
          </div>
          <span className={`text-xs font-mono ${getPerformanceColor(fps)}`}>
            {fps} FPS
          </span>
        </div>

        <div className="pt-2 border-t border-slate-600/50">
          <div className="text-xs text-slate-300 font-mono">
            STATUS: {isInitialized && audioContextState === 'running' ? '🟢 OPERATIONAL' :
                     isInitialized && audioContextState === 'suspended' ? '🟡 CLICK TO START' :
                     initializationError ? '🔴 FAULT DETECTED' :
                     '🟡 INITIALIZING...'}
          </div>
          {initializationError && (
            <div className="text-xs text-red-400 mt-1 font-mono">
              {initializationError}
            </div>
          )}
        </div>
      </div>
    </Panel>
  )
}

export default SystemStatusPanel